import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Edit, Trash2, Users, DollarSign } from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import { 
  createTeam,
  updateTeam,
  deleteTeam,
  fetchTeams
} from '@/store/slices/teamsSlice';
import { 
  Button, 
  Card, 
  CardContent, 
  CardHeader,
  CardTitle,
  Badge, 
  Modal, 
  ConfirmModal,
  DataTable
} from '@/components/ui';
import TeamForm from './TeamForm';
import type { Program, Team, Column, CostProfile } from '@/types';
import { costProfilesApi } from '@/utils/api';

interface TeamManagementProps {
  program: Program;
  teams: Team[];
  onClose: () => void;
}

const TeamManagement: React.FC<TeamManagementProps> = ({
  program,
  teams,
  onClose
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isCreating, isUpdating, isDeleting } = useSelector((state: RootState) => state.teams);
  const [costProfiles, setCostProfiles] = useState<CostProfile[]>([]);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);

  useEffect(() => {
    const loadCostProfiles = async () => {
      const response = await costProfilesApi.getAll();
      if (response.success && response.data) {
        setCostProfiles((response.data as any).costProfiles || response.data);
      }
    };
    loadCostProfiles();
  }, []);

  const handleCreateTeam = async (data: Partial<Team>) => {
    const teamData = { ...data, programId: program.id };
    const result = await dispatch(createTeam(teamData));
    if (createTeam.fulfilled.match(result)) {
      setIsCreateModalOpen(false);
      // Refresh teams for this program
      dispatch(fetchTeams({ programId: program.id }));
    }
  };

  const handleUpdateTeam = async (data: Partial<Team>) => {
    if (!selectedTeam) return;
    const result = await dispatch(updateTeam({ id: selectedTeam.id, data }));
    if (updateTeam.fulfilled.match(result)) {
      setIsEditModalOpen(false);
      setSelectedTeam(null);
      // Refresh teams for this program
      dispatch(fetchTeams({ programId: program.id }));
    }
  };

  const handleDeleteTeam = async () => {
    if (!selectedTeam) return;
    const result = await dispatch(deleteTeam(selectedTeam.id));
    if (deleteTeam.fulfilled.match(result)) {
      setIsDeleteModalOpen(false);
      setSelectedTeam(null);
      // Refresh teams for this program
      dispatch(fetchTeams({ programId: program.id }));
    }
  };

  const standardRates: Record<string, number> = {
    onshore: 150,
    offshore: 27,
    blended: 89,
  };

  const getRateForProfile = (costProfileId?: string, costProfile?: CostProfile | null) => {
    const profile = costProfiles.find(cp => cp.id === costProfileId) || costProfile;
    if (!profile) return 0;
    const nameKey = profile.name.toLowerCase();
    const standard = standardRates[nameKey];
    return standard ?? profile.hourlyRate ?? 0;
  };

  const calculateTeamCost = (team: Team) => {
    if (team.costAllocations && team.costAllocations.length > 0) {
      const totalCost = team.costAllocations.reduce((sum, alloc) => {
        return sum + (getRateForProfile(alloc.costProfileId, alloc.costProfile || undefined) * alloc.resourceCount * 40);
      }, 0);
      return totalCost;
    }

    const rate = getRateForProfile(team.costProfileId, team.costProfile || undefined);
    return team.memberCount * rate * 40;
  };

  const totalProgramCost = teams.reduce((sum, team) => sum + calculateTeamCost(team), 0);
  const totalMembers = teams.reduce((sum, team) => sum + team.memberCount, 0);

  // Table columns for teams
  const columns: Column<Team>[] = [
    {
      key: 'name',
      header: 'Team Name',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-foreground">{value}</div>
      )
    },
    {
      key: 'memberCount',
      header: 'Members',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-1">
          <Users className="w-4 h-4 text-muted-foreground" />
          <span>{value}</span>
        </div>
      )
    },
    {
      key: 'costProfile',
      header: 'Cost Profile',
      render: (value) => value?.name || 'Not assigned'
    },
    {
      key: 'costProfile',
      header: 'Planned Weekly',
      render: (value, row) => (
        <div className="flex items-center gap-1">
          <DollarSign className="w-4 h-4 text-muted-foreground" />
          <span>${calculateTeamCost(row).toLocaleString()}</span>
        </div>
      )
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (value) => (
        <Badge variant={value ? 'success' : 'secondary'}>
          {value ? 'Active' : 'Inactive'}
        </Badge>
      )
    }
  ];

  const tableActions = [
    {
      label: 'Edit',
      icon: Edit,
      onClick: (team: Team) => {
        setSelectedTeam(team);
        setIsEditModalOpen(true);
      }
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger' as const,
      onClick: (team: Team) => {
        setSelectedTeam(team);
        setIsDeleteModalOpen(true);
      }
    }
  ];

  return (
    <div className="space-y-6">
      {/* Program Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-accent-blue">{teams.length}</div>
              <div className="text-sm text-muted-foreground">Teams</div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-violet-400">{totalMembers}</div>
              <div className="text-sm text-muted-foreground">Total Members</div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-status-success">
                ${totalProgramCost.toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">Planned Weekly</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Teams Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Teams in {program.name}</CardTitle>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              leftIcon={<Plus className="w-4 h-4" />}
              size="sm"
            >
              Add Team
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          {teams.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">No Teams</h3>
              <p className="text-muted-foreground mb-4">Add teams to this program to manage capacity</p>
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Add First Team
              </Button>
            </div>
          ) : (
            <DataTable
              data={teams}
              columns={columns}
              actions={tableActions}
              searchable={false}
              pagination={false}
              emptyMessage="No teams found"
            />
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>

      {/* Create Team Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title={`Add Team to ${program.name}`}
        size="lg"
      >
        <TeamForm
          programId={program.id}
          programs={[program]}
          costProfiles={costProfiles}
          onSubmit={handleCreateTeam}
          onCancel={() => setIsCreateModalOpen(false)}
          isLoading={isCreating}
        />
      </Modal>

      {/* Edit Team Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedTeam(null);
        }}
        title="Edit Team"
        size="lg"
      >
        <TeamForm
          initialData={selectedTeam}
          programId={program.id}
          programs={[program]}
          costProfiles={costProfiles}
          onSubmit={handleUpdateTeam}
          onCancel={() => {
            setIsEditModalOpen(false);
            setSelectedTeam(null);
          }}
          isLoading={isUpdating}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedTeam(null);
        }}
        onConfirm={handleDeleteTeam}
        title="Delete Team"
        message={`Are you sure you want to delete "${selectedTeam?.name}"? This will remove all allocations and cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default TeamManagement;
